import { Button, Checkbox, Heading, Icon, IconButton, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Popover, PopoverArrow, PopoverBody, PopoverCloseButton, PopoverContent, PopoverHeader, PopoverTrigger, Select, Stack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useDisclosure, useToast } from "@chakra-ui/react";
import { useState } from "react";

import { BiTransfer } from "react-icons/bi";
import { FaArrowLeft, FaArrowRight, FaColumns, FaFileExcel, FaFilter, FaSync, FaUsers } from "react-icons/fa";
import { FcCheckmark } from 'react-icons/fc';

import { RiMailAddFill } from "react-icons/ri";
import BlockUserButton from "./BlockUserModal";
import EditUserButton from "./EditUserModal";
import RemoveUserButton from "./RemoveUserModal";
import { POST } from "../../../utils/ApiRequestProvider";

type ColKey = 'ref' | 'businessName' | 'businessShortName' | 'entityEmail' | 'type' | 'vatGST' | 'accountingSoftware' | 'processing' | 'processed' | 'approvedExported' | 'lastUpload';

export default function PracticeStaff() {
    const toast = useToast();
    const { isOpen: isTransferOpen, onOpen: onTransferOpen, onClose: onTransferClose } = useDisclosure();
    const { isOpen: isInviteOpen, onOpen: onInviteOpen, onClose: onInviteClose } = useDisclosure();
    const [transferEmail, setTransferEmail] = useState('');
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('member');
    const [, setShowFilters] = useState(false);
    const [visibleCols, setVisibleCols] = useState<Record<ColKey, boolean>>({
        ref: true, businessName: true, businessShortName: true, entityEmail: true, type: true,
        vatGST: true, accountingSoftware: true, processing: true, processed: true, approvedExported: true, lastUpload: true,
    });
    const toggleCol = (k: ColKey) => setVisibleCols((p) => ({ ...p, [k]: !p[k] }));

    const handleTransfer = async () => {
        if (!transferEmail.trim()) return;
        try {
            const res = await POST('/api/Practice/transferOwnership', { email: transferEmail });
            toast({ title: res.status === 'success' ? 'Ownership transferred' : (res.message || 'Transfer failed'), status: res.status === 'success' ? 'success' : 'error', duration: 1500, position: 'top-right' });
            if (res.status === 'success') {
                setTransferEmail('');
                onTransferClose();
            }
        } catch {
            toast({ title: 'Transfer failed', status: 'error', duration: 1500, position: 'top-right' });
        }
    };
    const handleInvite = async () => {
        if (!inviteEmail.trim()) return;
        try {
            const res = await POST('/api/Practice/invite', { email: inviteEmail, role: inviteRole });
            toast({ title: res.status === 'success' ? 'Invitation sent' : (res.message || 'Invite failed'), status: res.status === 'success' ? 'success' : 'error', duration: 1500, position: 'top-right' });
            if (res.status === 'success') {
                setInviteEmail('');
                onInviteClose();
            }
        } catch {
            toast({ title: 'Invite failed', status: 'error', duration: 1500, position: 'top-right' });
        }
    };
    const handleRefresh = () => window.location.reload();

    const [invs] = useState([
        {
            ref: 1,
            businessName: "Inaam Space Research Organization",
            businessShortName: "ISRO",
            entityEmail: "info@isro.org",
            type: "Non Profit Organization",
            vatGST: '-',
            accountingSoftware: <Icon as={FaFileExcel} color={'#4C8766'} w={'15px'} h={'15px'} />,
            processing: 12,
            processed: 6,
            approvedExported: 3,
            lastUpload: 17,
            newReporting: ''
        },
        {
            ref: 2,
            businessName: "Insight Ways Private Limited",
            businessShortName: "IWPL",
            entityEmail: "hello@entry-bot.com",
            type: "Limited Company",
            vatGST: <Icon as={FcCheckmark} color={'#4C8766'} w={'15px'} h={'15px'} />,
            accountingSoftware: <Icon as={FaFileExcel} color={'#4C8766'} w={'15px'} h={'15px'} />,
            processing: 12,
            processed: 6,
            approvedExported: 3,
            lastUpload: 17,
            newReporting: ''
        }
    ]);

    return (
        <Stack w={'full'} justify={"center"} align={"center"} spacing={6}>
            <Stack
                direction={{ base: 'column', md: 'column', lg: 'column', xl: 'row' }}
                justify={'space-between'}
                boxShadow={'base'}
                rounded={'xl'}
                w={'98%'}
                p={4}
                spacing={{ base: 6, md: 6, lg: 6, xl: 0 }}
            >
                <Stack
                    align={'flex-start'}
                    justify={'start'}
                    spacing={4}
                >
                    <Stack direction={'row'}>
                        <Icon
                            as={FaUsers}
                            fontSize={'5xl'}
                            color={'primary.400'}
                        />
                        <Stack spacing={1}>
                            <Heading fontSize={'xl'}>Practice Staff</Heading>
                            <Text fontSize={'sm'}>Manage access and user roles of full team members of your practice.</Text>
                        </Stack>
                    </Stack>
                </Stack>
                <Stack align={{ lg: 'flex-start', xl: 'flex-end' }} justify={'end'} spacing={4}>
                    <Stack direction={'row'}>
                        <Button
                            size={'sm'}
                            leftIcon={<BiTransfer />}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                            onClick={onTransferOpen}
                        >
                            Transfer Ownership
                        </Button>
                        <Button
                            size={'sm'}
                            leftIcon={<RiMailAddFill />}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                            onClick={onInviteOpen}
                        >
                            Invite
                        </Button>
                        <Popover placement={'bottom-end'}>
                            <PopoverTrigger>
                                <Button
                                    size={'sm'}
                                    leftIcon={<FaColumns />}
                                    color={'white'}
                                    bg={'primary.400'}
                                    _hover={{ bg: 'primary.400' }}
                                    _active={{ bg: 'primary.400' }}
                                >
                                    Columns
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent>
                                <PopoverArrow />
                                <PopoverCloseButton />
                                <PopoverHeader>Visible columns</PopoverHeader>
                                <PopoverBody as={Stack} spacing={2}>
                                    {(Object.keys(visibleCols) as ColKey[]).map((k) => (
                                        <Checkbox key={k} isChecked={visibleCols[k]} onChange={() => toggleCol(k)}>{k}</Checkbox>
                                    ))}
                                </PopoverBody>
                            </PopoverContent>
                        </Popover>
                        <Button
                            size={'sm'}
                            leftIcon={<FaSync />}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                            onClick={handleRefresh}
                        >
                            Refresh
                        </Button>
                        <Button
                            size={'sm'}
                            leftIcon={<FaFilter />}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                            onClick={() => setShowFilters((v) => !v)}
                        >
                            Filter
                        </Button>
                    </Stack>
                </Stack>
            </Stack>

            <Stack boxShadow={'base'} rounded={'xl'} w={'98%'} p={4} spacing={4}>
                <TableContainer
                    css={{
                        '&::-webkit-scrollbar-thumb': {
                            background: '#5E5E5E',
                        },
                    }}
                >
                    <Table
                        size='sm'
                    >
                        <Thead  position={'sticky'} top={'0%'} h={'50px'} bg={'#5E5E5E'} alignItems={'flex-start'}>
                            <Tr>
                                <Th
                                    color={'white'}
                                >
                                    Actions
                                </Th>
                                <Th
                                    color={'white'}
                                    isNumeric
                                >
                                    Ref
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Business Name
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Business Short Name
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Entity Email
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Type
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    VAT/GST
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Accounting Software
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Processing
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Processed
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Approved / Exported
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Days Since Last Upload
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    New Reporting
                                </Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {
                                invs.map(inv => (
                                    <Tr>
                                        <Td as={Stack} spacing={1} direction={'row'}>
                                            <EditUserButton />
                                            
                                            <BlockUserButton />

                                            <RemoveUserButton />
                                        </Td>
                                        <Td isNumeric>{inv.ref}</Td>
                                        <Td>{inv.businessName}</Td>
                                        <Td>{inv.businessShortName}</Td>
                                        <Td>{inv.entityEmail}</Td>
                                        <Td>{inv.type}</Td>
                                        <Td textAlign={'center'}>{inv.vatGST}</Td>
                                        <Td textAlign={'center'}>{inv.accountingSoftware}</Td>
                                        <Td isNumeric>{inv.processing}</Td>
                                        <Td isNumeric>{inv.processed}</Td>
                                        <Td isNumeric>{inv.approvedExported}</Td>
                                        <Td isNumeric>{inv.lastUpload}</Td>
                                        <Td isNumeric>{inv.newReporting}</Td>
                                    </Tr>
                                ))
                            }
                        </Tbody>
                    </Table>
                </TableContainer>
                <Stack
                    direction={'row'}
                    justify={"space-between"}
                    align={'center'}
                >
                    <Text>Showing 5 of 10 results</Text>
                    <Stack direction={'row'} spacing={4}>
                        <Select placeholder="Select Rows Per Page">
                            <option selected>5 Per Page</option>
                            <option>10 Per Page</option>
                            <option>25 Per Page</option>
                            <option>50 Per Page</option>
                        </Select>
                        <Stack direction={'row'}>
                            <IconButton
                                aria-label="Previous"
                                icon={<FaArrowLeft />}
                                rounded={'full'}
                            />
                            <IconButton
                                aria-label="Previous"
                                icon={<FaArrowRight />}
                                rounded={'full'}
                            />
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
            <Modal isOpen={isTransferOpen} onClose={onTransferClose} isCentered>
                <ModalOverlay bg={'blackAlpha.400'} backdropFilter={'blur(6px)'} />
                <ModalContent>
                    <ModalHeader>Transfer Ownership</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text fontSize={'sm'} color={'gray.600'} mb={2}>Email of the new owner:</Text>
                        <Input value={transferEmail} onChange={(e) => setTransferEmail(e.target.value)} placeholder={'owner@example.com'} />
                    </ModalBody>
                    <ModalFooter>
                        <Button variant={'ghost'} mr={2} onClick={onTransferClose}>Cancel</Button>
                        <Button colorScheme={'red'} onClick={handleTransfer}>Transfer</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            <Modal isOpen={isInviteOpen} onClose={onInviteClose} isCentered>
                <ModalOverlay bg={'blackAlpha.400'} backdropFilter={'blur(6px)'} />
                <ModalContent>
                    <ModalHeader>Invite team member</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody as={Stack} spacing={3}>
                        <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder={'teammate@example.com'} />
                        <Select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                            <option value={'member'}>Member</option>
                            <option value={'admin'}>Admin</option>
                            <option value={'viewer'}>Viewer</option>
                        </Select>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant={'ghost'} mr={2} onClick={onInviteClose}>Cancel</Button>
                        <Button colorScheme={'blue'} onClick={handleInvite}>Send invite</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Stack >
    )
}